export const SHIPMENT_STATUS = {
  PENDING: 'PENDING',
  QUOTING: 'QUOTING',
  CONFIRMED: 'CONFIRMED',
  CONFIRMED_PENDING_PAYMENT: 'CONFIRMED_PENDING_PAYMENT',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  PAID: 'PAID',
  EN_ROUTE_TO_PICKUP: 'EN_ROUTE_TO_PICKUP',
  COMPLETED: 'COMPLETED',
  IN_TRANSIT: 'IN_TRANSIT',
  DELIVERED: 'DELIVERED',
  PAYOUT_PENDING: 'PAYOUT_PENDING',
  PAYOUT_COMPLETED: 'PAYOUT_COMPLETED',
  CANCELLED: 'CANCELLED',
  // NEX-1381: written by nexvia-api-web's expire-shipments job when the pickup window closes.
  // It used to be derived here from expiresAt, because the API sent nothing for it.
  EXPIRED: 'EXPIRED'
} as const;

export type ShipmentStatus = (typeof SHIPMENT_STATUS)[keyof typeof SHIPMENT_STATUS];

export const SHIPMENT_TYPES = {
  PACKAGES: 'packages',
  VEHICLES: 'vehicles',
  MOVING: 'moving',
  PASSENGERS: 'passengers',
  // NEX-1151: "Guardado" — unlike the other types, a storage request never becomes a row in
  // this app's `/shipments` table at all (it's POST /storage/requests, a separate resource).
  // It still needs a shipmentKind value in the wizard's own formData, a label here and an
  // entry in the type registry (shipment.types.ts) for RequestTypeSelector/StepReview. It
  // runs through the cargo wizard, so it is a CargoShipmentType.
  STORAGE: 'storage'
} as const;

export type ShipmentType = (typeof SHIPMENT_TYPES)[keyof typeof SHIPMENT_TYPES];
export type CargoShipmentType = Exclude<ShipmentType, typeof SHIPMENT_TYPES.PASSENGERS>;

export const SHIPMENT_TYPE_LABEL: Record<ShipmentType, string> = {
  [SHIPMENT_TYPES.PACKAGES]: 'Paquetes',
  [SHIPMENT_TYPES.VEHICLES]: 'Vehículos',
  [SHIPMENT_TYPES.MOVING]: 'Mudanza',
  [SHIPMENT_TYPES.PASSENGERS]: 'Personas',
  // Johan's decision (plan 2026-10-20, answer 7): the client web calls the module "Depósitos", never "Guardado".
  [SHIPMENT_TYPES.STORAGE]: 'Depósitos'
};

export const PACKAGE_TYPES = {
  SMALL: 'Small',
  MEDIUM: 'Medium',
  LARGE: 'Large',
  CUSTOM: 'Custom'
} as const;

export type PackageType = (typeof PACKAGE_TYPES)[keyof typeof PACKAGE_TYPES];

export const PACKAGE_TYPE_LABEL: Record<PackageType, string> = {
  [PACKAGE_TYPES.SMALL]: 'Pequeño',
  [PACKAGE_TYPES.MEDIUM]: 'Mediano',
  [PACKAGE_TYPES.LARGE]: 'Grande',
  [PACKAGE_TYPES.CUSTOM]: 'Customizado'
};

// Per-axis ceiling for Alto, Ancho and Largo, in centimetres. Only a Customizado package can
// reach it — the other three types write their preset measurements into the fields. It bounds
// both the steppers and getValidationErrors in StepDetailsPackages, and it mirrors
// MAX_PACKAGE_DIMENSION_CM in nexvia-api-web
// (`src/modules/shipment/constants/package.constants.ts`). The API rejects anything above its
// own copy, so raising this one alone only moves the refusal later in the flow.
export const MAX_PACKAGE_DIMENSION_CM = 1000;

// How many photos a Paquetes request can carry, counted for the whole request rather than per
// package: the API stores photos per shipment and keeps no link to a package row. Vehicles use
// the same number per vehicle (`MAX_PHOTOS` in StepDetailsVehicle). The API has no limit of its
// own for any category, so this is the only place it is enforced.
export const MAX_PACKAGE_PHOTOS = 5;

export const VEHICLE_CATEGORY_LABEL: Record<string, string> = {
  motorcycle: 'Moto',
  car: 'Auto',
  utv: 'Utilitarios',
  pickup: 'Camioneta',
  suv: 'SUV'
};

export const CLOSED_REASON = {
  QUOTE_CONFIRMATION: 'QUOTE_CONFIRMATION',
  PICKUP_WINDOW_START: 'PICKUP_WINDOW_START',
  // NEX-1381: the reason the expire-shipments job actually writes. PICKUP_WINDOW_START above is
  // written nowhere in the API — it exists only in its enum and in the analytics constants — so
  // the expiry message below was keyed to a code that never arrived.
  PICKUP_WINDOW_END: 'PICKUP_WINDOW_END',
  CANCELLED_BY_CLIENT: 'CANCELLED_BY_CLIENT',
  NO_SHOW: 'NO_SHOW'
} as const;

export type ClosedReason = (typeof CLOSED_REASON)[keyof typeof CLOSED_REASON];

export const CLOSED_REASON_LABEL: Partial<Record<ClosedReason, string>> = {
  [CLOSED_REASON.QUOTE_CONFIRMATION]: 'Esta publicación fue cerrada porque ya se confirmó una cotización.',
  [CLOSED_REASON.PICKUP_WINDOW_START]: 'Esta publicación venció porque se alcanzó la fecha de retiro.',
  [CLOSED_REASON.PICKUP_WINDOW_END]: 'Esta publicación venció porque se alcanzó la fecha de retiro.',
  [CLOSED_REASON.NO_SHOW]: 'El grupo no se presentó a la hora de salida.'
};
